import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import orderBy from 'lodash/orderBy';
import isEqual from 'lodash/isEqual';
import { segment } from '@actions/analytics';
import { PLANNER_CALENDAR_VIEWED, PLANNER_PAGE } from '@constants/segment';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import {
  getDateKey,
  getTodayDate
} from '@cont/PlannerV2/planner-utils/date-time-helper';
import GroupsContainer from '@cont/Groups/GroupsContainer';
import PlannerErrorBoundary from '@cont/PlannerV2/PlannerErrorBoundary';
import CalendarSleek from '@icon/CalendarSleek';
import Menu from '@base/Menu/Menu';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useGetGroupsInfo from '@cont/Groups/groups-hooks/useGroupInfo';
import useRequestStatusStore from '@stores/requestStatus';
import {
  getUberLoaderVisibility,
  getUberLoaderVisibilityUpdater
} from '@stores/selectors/requestStatus';
import {
  getFeatureCards,
  getShowBrowse,
  getDataV2,
  getCalendarDateSetterV2
} from '@stores/selectors/planner';
import useFeaturesAndBrowseCardStore from '@stores/planner/featuresAndBrowseCardStore';
import { useInfoStoreV2 } from '@stores/planner/infoStore';
import Dynamic from '@base/Dynamic/Dynamic';
import useCompeteConfig from '@cont/Compete/useCompeteConfig';
import { COMPETE_STATES } from '@cont/Compete/constants';
import useFreeTrialStore from '@cont/NewPreSubscribed/FreeTrial/freeTrialStore';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useFreeTrial from '@cont/NewPreSubscribed/FreeTrial/useFreeTrial';
import { useCalendarStoreV2 } from '@stores/planner/calendarStore';
import { compareDates } from '@utils/dates';
import usePlanner from './planner-hooks/usePlanner';
import useGetPlannerHeight from './planner-hooks/useGetPlannerHeight';
import TodayPill from './PlannerCards/TodayPill';
import PlannerItemList from './PlannerItemList';
import Calendar from './Calendar';
import ReferFriendsCard from './ReferFriendsCard';
import SettingUpPlanner from './SettingUpPlanner';
import ContinueWatchingSection from './ContinueWatchingSection/ContinueWatchingSection';

const RankersDataForm = Dynamic(() =>
  import('@cont/RankersFormData/RankersForm')
);

const ActiveSubscriptionModal = Dynamic(() =>
  import('@cont/NewPreSubscribed/FreeTrial/ActiveSubscriptionModal')
);

const Wrapper = styled.div`
  grid-column: 1 / -1;
  position: relative;
`;

const RightWrapper = styled.div`
  position: absolute;
  top: ${Spacings.SPACING_10B};
  right: ${Spacings.SPACING_4B};
  height: max-content;
  pointer-events: none;
  ${mediaQuery.uptoMobile} {
    grid-column: 16 / 25;
    display: none;
  }
`;

const CalendarWrapper = styled.div`
  position: absolute;
  top: ${Spacings.SPACING_10B};
  right: -${Spacings.SPACING_14B};
  height: max-content;
  pointer-events: none;
  ${mediaQuery.uptoMobile} {
    grid-column: 16 / 25;
    display: none;
  }
`;

const StyPopper = styled(Menu)`
  .MuiPaper-rounded {
    z-index: 1301;
    margin-top: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_8B};
    box-shadow: ${boxShadowGenerator()};
    border-radius: ${Spacings.SPACING_2B};
    background: var(--color-base-1);
  }
`;

const ButtonWrapper = styled.div`
  position: fixed;
  top: ${({ $isCompete }) => ($isCompete ? '170px' : '105px')};
  left: calc((100% - 350px - ((100vw - 1136px) / 2)) - 143px);
`;

const StyledButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-self: center;
  align-items: center;
  line-height: 150%;
  cursor: pointer;
  transition: background-color 400ms ease-in-out;
  border: none;
  border-radius: ${Spacings.SPACING_2B};
  background-color: ${({ $open }) =>
    $open ? 'var(--color-divider);' : 'transparent;'};
  color: var(--color-text-primary);
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
  font-size: ${Fonts.P1};
  font-weight: ${Fonts.SEMIBOLD};
  height: 36px;
  &:hover {
    background-color: var(--color-divider);
  }
`;

const StyledCalendar = styled(CalendarSleek)`
  margin-right: ${Spacings.SPACING_2B};
`;

const Planner = () => {
  const todayKey = getDateKey(getTodayDate().date);
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);

  const {
    query: { goalUID }
  } = useRouter();

  const getTodayDirection = () => {
    const isFutureDate = compareDates(todayKey, selectedDateKey) === 1;
    const isPastDate = compareDates(todayKey, selectedDateKey) === -1;

    if (isFutureDate) return 'left';
    if (isPastDate) return 'right';
  };

  const containerHeight = useGetPlannerHeight();

  const { isInitialCheckComplete, refreshFeatureCards } =
    usePlanner(selectedDateKey);
  const { groupsEnabledStatus, groupUID } = useGetGroupsInfo(goalUID);

  const {
    plannerItemsByDays: plannerItemsByDaysV2,
    datesAndKeys: unsortedDatesAndKeys
  } = useInfoStoreV2(getDataV2());

  const goalSegmentData = useGoalEventProps({ goalUID });

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarPopupAnchorEl, setCalendarPopupAnchorEl] = useState(null);

  const { isCompete, state } = useCompeteConfig();
  const isCompetePostLaunch = state === COMPETE_STATES.POST_LAUNCH && isCompete;

  const initalDatesAndKeys = orderBy(unsortedDatesAndKeys, 'date', 'asc');
  const [datesAndKeys, setDatesAndKeys] = useState(initalDatesAndKeys);
  useEffect(() => {
    const newDateKeys = orderBy(unsortedDatesAndKeys, 'date', 'asc');
    const hasItemsChanged = !isEqual(newDateKeys, datesAndKeys);
    if (hasItemsChanged) {
      setDatesAndKeys(newDateKeys);
    }
  }, [unsortedDatesAndKeys, datesAndKeys]);

  const isActivateFreeTrialClicked = useFreeTrialStore(
    (storeState) => storeState.buttonClicked
  );
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { hasFreeTrialSubscription } = useFreeTrial(goalUID);

  const showBrowse = useFeaturesAndBrowseCardStore(
    getShowBrowse(goalUID, todayKey)
  );
  const featureCards = useFeaturesAndBrowseCardStore(
    getFeatureCards(goalUID, todayKey)
  );

  const setCalendarDate = useCalendarStoreV2(getCalendarDateSetterV2);

  const handleSelectedDateSet = (newDate) => {
    setSelectedDateKey(newDate);
    setCalendarDate(new Date(newDate)?.getTime());
  };

  const goToToday = () => {
    handleSelectedDateSet(todayKey);
  };

  const onDateSelected = (selectedDate) => {
    setCalendarPopupAnchorEl(null);
    setCalendarOpen(false);
    handleSelectedDateSet(selectedDate);
  };

  const showUberLoader = useRequestStatusStore(getUberLoaderVisibility);
  const setShowUberLoader = useRequestStatusStore(
    getUberLoaderVisibilityUpdater
  );

  useEffect(() => {
    if (!isInitialCheckComplete) {
      setShowUberLoader(true);
    } else if (showUberLoader) {
      setShowUberLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialCheckComplete]);

  if (!isInitialCheckComplete)
    return <SettingUpPlanner height={containerHeight} />;

  const onCalendarButtonClicked = (event) => {
    setCalendarOpen((prevCalendarOpen) => !prevCalendarOpen);
    setCalendarPopupAnchorEl(event.currentTarget);
    if (!calendarOpen) {
      segment.track(PLANNER_CALENDAR_VIEWED, {
        ...goalSegmentData,
        last_primary_source: PLANNER_PAGE,
        last_primary_source_section: 'Planner'
      });
    }
  };

  const showFreeTrialNudge =
    isLoggedIn && isActivateFreeTrialClicked && hasFreeTrialSubscription;

  return (
    <>
      <Wrapper>
        <RankersDataForm />
        <PlannerErrorBoundary>
          <>
            <TodayPill
              show={todayKey !== selectedDateKey}
              goToToday={goToToday}
              todayDirection={getTodayDirection()}
            />
            <PlannerItemList
              showBrowse={showBrowse}
              featureCards={featureCards}
              todayKey={todayKey}
              plannerItemsByDaysV2={plannerItemsByDaysV2}
              selectedDateKey={selectedDateKey}
              refreshFeatureCards={refreshFeatureCards}
            />
          </>
        </PlannerErrorBoundary>
        <RightWrapper>
          {groupsEnabledStatus === 'enabled' && (
            <PlannerErrorBoundary>
              <GroupsContainer groupUID={groupUID} />
              <ReferFriendsCard groupUID={groupUID} />
            </PlannerErrorBoundary>
          )}
        </RightWrapper>
        <CalendarWrapper>
          {groupsEnabledStatus === 'disabled' && (
            <PlannerErrorBoundary>
              <Calendar
                dateSelected={onDateSelected}
                datesAndKeys={datesAndKeys}
              />
              <ReferFriendsCard groupUID={groupUID} />
            </PlannerErrorBoundary>
          )}
        </CalendarWrapper>
        <PlannerErrorBoundary>
          <ContinueWatchingSection />
        </PlannerErrorBoundary>
        {groupsEnabledStatus === 'enabled' && (
          <ButtonWrapper $isCompete={isCompetePostLaunch}>
            <StyledButton
              $open={calendarOpen}
              type="button"
              onClick={onCalendarButtonClicked}
            >
              <StyledCalendar
                size="20px"
                aria-hidden="true"
                color="var(--color-text-primary)"
              />
              Calendar
            </StyledButton>
            <StyPopper
              open={calendarOpen}
              anchorEl={calendarPopupAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              onClose={onDateSelected}
              width={484}
              height={424}
            >
              <Calendar
                datesAndKeys={datesAndKeys}
                dateSelected={onDateSelected}
              />
            </StyPopper>
          </ButtonWrapper>
        )}
      </Wrapper>
      <ActiveSubscriptionModal open={showFreeTrialNudge} />
    </>
  );
};

export default Planner;
